<script setup lang="ts">
import { onMounted, ref, type PropType } from 'vue';
import { useContractTypeStore } from '@/stores/contractTypeStore';
import type { CalendarPreviewFilters } from '@/utils/calendarOccurrences';
import GaiaCheckbox from '../../../base/GaiaCheckbox.vue';
import GaiaSearchInput from '../../../base/inputs/GaiaSearchInput.vue';
import GaiaSelect from '../../../base/selects/GaiaSelect.vue';

/**
 * The search and flags above a calendar preview. Lives outside both views so
 * one search narrows the calendar and the list beneath it alike. The search
 * text only reaches `filters` once the input's debounce fires.
 */
const props = defineProps({
  filters: {
    type: Object as PropType<CalendarPreviewFilters>,
    required: true,
  },
});

const emit = defineEmits<{ 'update:filters': [filters: CalendarPreviewFilters] }>();

const draft = ref<string>(props.filters.text);

const contractTypeStore = useContractTypeStore();

onMounted(() => {
  void contractTypeStore.ensureLoaded();
});

function patch(changes: Partial<CalendarPreviewFilters>): void {
  emit('update:filters', { ...props.filters, ...changes });
}
</script>

<template>
  <n-flex align="center" :size="[16, 10]">
    <gaia-search-input
      v-model:value="draft"
      placeholder="Search contracts"
      :debounce="500"
      @search="(value: string) => patch({ text: value })"
    />
    <gaia-select
      :value="props.filters.types"
      multiple
      clearable
      max-tag-count="responsive"
      placeholder="All types"
      filterable
      :options="contractTypeStore.nameOptions"
      :style="{ width: '180px' }"
      @update:value="(value: string[]) => patch({ types: value })"
    />
    <n-flex align="center" :size="16" :wrap="false" :style="{ marginLeft: 'auto' }">
      <gaia-checkbox
        :checked="props.filters.onlyValueExtracted"
        @update:checked="(checked: boolean) => patch({ onlyValueExtracted: checked })"
      >
        Only extracted values
      </gaia-checkbox>
      <gaia-checkbox
        :checked="props.filters.onlyFuture"
        @update:checked="(checked: boolean) => patch({ onlyFuture: checked })"
      >
        Only future
      </gaia-checkbox>
    </n-flex>
  </n-flex>
</template>
